import { Box, Divider, Group, Stack, Text } from '@mantine/core'
import { CheckFatIcon, StarIcon } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import { getBoggleChallengeScoreRank } from '~/boggle/lib/challenge/scoreRank'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import strikeStyles from './ChallengeScoreTargets.module.css'

function CrossedChallengeScoreBlock({
  active,
  children,
}: {
  active: boolean
  children: ReactNode
}) {
  if (!active) {
    return children
  }

  return (
    <Box className={strikeStyles.strike} pos="relative">
      <Box className={strikeStyles.inner}>{children}</Box>
    </Box>
  )
}

/** Target / Ace / Best row — centered, single fixed style (historical staging footer layout). */
export function ChallengeScoreTargetsHud({
  aceScore,
  bestScore,
  showScoreStatusIcons = true,
  targetScore,
  trimBottomSpacing = false,
}: {
  aceScore?: number
  bestScore: null | number
  showScoreStatusIcons?: boolean
  targetScore?: number
  /** When embedded (e.g. header tray with its own padding), omit footer margin under the row */
  trimBottomSpacing?: boolean
}) {
  const stackMb = trimBottomSpacing ? 0 : 8
  const hasChallengeTargets =
    typeof aceScore === 'number' && typeof targetScore === 'number'

  if (hasChallengeTargets) {
    const scoreRank =
      bestScore === null
        ? 'below-target'
        : getBoggleChallengeScoreRank(bestScore, {
            aceScore,
            targetScore,
          })
    const reachedTarget = scoreRank === 'ace' || scoreRank === 'target'
    const reachedAce = scoreRank === 'ace'
    const crossOutTarget = reachedTarget
    const crossOutAce = reachedAce

    return (
      <Stack align="center" gap={3} mb={stackMb} pos="relative">
        <Group align="center" gap={12} justify="center" wrap="nowrap">
          <CrossedChallengeScoreBlock active={crossOutTarget}>
            <Stack gap={6} align="center" w={42}>
              <Text c="white" opacity={0.7} fz={11} lts="0.12em" tt="uppercase">
                Goal
              </Text>
              <Text c="white" fz={18} fw={700} lts="0.12em" tt="uppercase">
                {targetScore}
              </Text>
            </Stack>
          </CrossedChallengeScoreBlock>
          {reachedTarget ? (
            <>
              <Divider orientation="vertical" color="white" opacity={0.2} />
              <CrossedChallengeScoreBlock active={crossOutAce}>
                <Stack gap={6} align="center" w={42}>
                  <Text
                    c={reachedAce ? 'white' : '#f0c85a'}
                    opacity={reachedAce ? 0.7 : 1}
                    fz={11}
                    lts="0.12em"
                    tt="uppercase"
                  >
                    Ace
                  </Text>
                  <Text c="white" fz={18} fw={700} lts="0.12em" tt="uppercase">
                    {aceScore}
                  </Text>
                </Stack>
              </CrossedChallengeScoreBlock>
            </>
          ) : null}
          <>
            <Divider orientation="vertical" color="white" opacity={0.2} />
            <Stack gap={6} align="center" w={42} pos="relative">
              <Text c="white" opacity={0.7} fz={11} lts="0.12em" tt="uppercase">
                Best
              </Text>
              <Group wrap="nowrap">
                <Text c="white" fz={18} fw={700} lts="0.12em" tt="uppercase" opacity={bestScore ? 1 : 0.3}>
                  {bestScore ?? '-'}
                </Text>
              </Group>
            </Stack>
          </>
        </Group>
        <Box
          pos="absolute"
          top={36}
          right="50%"
          style={{ transform: 'translateX(50%)' }}
        >
          {showScoreStatusIcons && bestScore != null ? (
            reachedAce ? (
              <IconGlyph color="#f0c85a" icon={StarIcon} size={36} weight="fill" />
            ) : null
          ) : null}
        </Box>
      </Stack>
    )
  }

  if (bestScore === null) {
    return null
  }

  return (
    <Stack align="center" gap={3} ff="'Tilt Neon', sans-serif" mb={stackMb}>
      <Group align="center" justify="center" wrap="nowrap">
        <Stack gap={6} align="center" w={40}>
          <Text c="white" opacity={0.7} fz={13} lts="0.12em" tt="uppercase">
            Best
          </Text>
          <Text c="white" fz={18} fw={700} lts="0.12em" tt="uppercase">
            {bestScore}
          </Text>
        </Stack>
      </Group>
    </Stack>
  )
}

export function ChallengeScoreTargets({
  aceScore,
  bestScore,
  showScoreStatusIcons = true,
  targetScore,
}: {
  aceScore?: number
  bestScore: null | number
  showScoreStatusIcons?: boolean
  targetScore?: number
}) {
  return (
    <ChallengeScoreTargetsHud
      aceScore={aceScore}
      bestScore={bestScore}
      showScoreStatusIcons={showScoreStatusIcons}
      targetScore={targetScore}
    />
  )
}
